import i18n from 'i18n-js';
import _ from 'lodash';

import agent from 'api/agent';
import {
    EmptyOrderModel,
    OrderItemStatusChangeModel,
    OrderUpdateModel,
    StornoOrderItemModel,
} from 'api/models';
import { OrderStatus, OrderItemStatus, OrderServices } from 'constants/enums';
import globalStore from '../globalStore';
import { AppThunkAction } from 'store';
import { OrdersAction } from './actions';
import { OrderDetails, OverdudedOrder, OrderReadyDishes, OrderItem } from './reducer';

export const actionCreators = {
    getOrdersList:
        (
            searchString: string = '',
            dateFrom: string | undefined = undefined,
            dateTo: string | undefined = undefined,
            page: number = 0,
            limit: number = 13,
            asc: boolean = false,
            orderBy: string = '',
            position: string = '',
            showClosed: boolean = false
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'GET_ORDERS_LIST_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.GetOrdersList(
                searchString,
                dateFrom,
                dateTo,
                page,
                limit,
                asc,
                orderBy,
                position,
                showClosed
            )
                .then((res) => {
                    dispatch({
                        type: 'GET_ORDERS_LIST_SUCCESS',
                        list: res.list,
                        count: res.count,
                        overduded: false,
                    });
                    dispatch({
                        type: 'SET_ORDER_DETAILS_ID',
                        id: res.list[0]?.id,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'GET_ORDERS_LIST_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    updateOrdersList:
        (
            searchString: string = '',
            dateFrom: string | undefined = undefined,
            dateTo: string | undefined = undefined,
            page: number = 0,
            limit: number = 13,
            asc: boolean = false,
            orderBy: string = ''
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'UPDATE_ORDERS_LIST_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.GetOrdersList(searchString, dateFrom, dateTo, page, limit, asc, orderBy)
                .then((res) => {
                    dispatch({
                        type: 'UPDATE_ORDERS_LIST_SUCCESS',
                        list: res.list,
                        count: res.count,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'UPDATE_ORDERS_LIST_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },

    setOrderDetailsId:
        (id: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_ORDER_DETAILS_ID',
                id: id,
            });
        },

    changeOrderItemStatus:
        (id: string, model: OrderItemStatusChangeModel): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'CHANGE_ORDER_ITEM_STATUS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.ChangeOrderItemStatus(id, model)
                .then((res) => {
                    dispatch({
                        type: 'CHANGE_ORDER_ITEM_STATUS_SUCCESS',
                        payload: res,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'CHANGE_ORDER_ITEM_STATUS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },

    updateOrderStatus:
        (id: string, status: OrderStatus): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'UPDATE_ORDER_STATUS',
                id: id,
                status: status,
            });
        },

    setSearchString:
        (data: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_SEARCH_STRING',
                data: data,
            });
        },

    getNewOrders:
        (position: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'GET_NEW_ORDERS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.GetNewOrdersCount(position)
                .then((res) => {
                    dispatch({
                        type: 'GET_NEW_ORDERS_SUCCESS',
                        newOrders: res,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'GET_NEW_ORDERS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    getPositionOrders:
        (position: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'GET_POSITION_ORDERS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.GetPositionOrders(position)
                .then((res) => {
                    dispatch({
                        type: 'GET_POSITION_ORDERS_SUCCESS',
                        data: res,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'GET_POSITION_ORDERS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    createEmptyOrder:
        (model: EmptyOrderModel): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'CREATE_EMPTY_ORDER_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.CreateEmptyOrder(model)
                .then((res) => {
                    dispatch({
                        type: 'CREATE_EMPTY_ORDER_SUCCESS',
                        data: res,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'CREATE_EMPTY_ORDER_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    changePositionOrderStatus:
        (
            orderId: string,
            model: OrderItemStatusChangeModel,
            previousStatus: OrderItemStatus
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'CHANGE_POSITION_ORDER_STATUS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            //@ts-ignore
            agent.Order.ChangeOrderItemStatus(orderId, model)
                .then((order: OrderDetails) => {
                    dispatch({
                        type: 'CHANGE_POSITION_ORDER_STATUS_SUCCESS',
                        order: order,
                    });
                })
                .catch((e) => {
                    dispatch({
                        type: 'CHANGE_POSITION_ORDER_STATUS_ERROR',
                        orderId,
                        previousStatus,
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => {
                    globalStore.actionCreators.hideSpiner()(dispatch);
                    setTimeout(() => {
                        dispatch({ type: 'IS_ORDER_ITEM_STATUS_CHANGING_STOP' });
                    }, 300);
                });
        },
    updateOrder:
        (id: string, model: OrderUpdateModel, action?: () => void): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'UPDATE_ORDER_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.UpdateOrder(id, model)
                .then((res) => {
                    dispatch({
                        type: 'UPDATE_ORDER_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                    action && action();
                })
                .catch((e) => {
                    dispatch({
                        type: 'UPDATE_ORDER_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    getOrderDetails:
        (
            id: string,
            setState?: (order: OrderDetails) => void,
            setInitialState?: (order: OrderDetails) => void,
            needToUpdate?: boolean
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'GET_ORDER_DETAILS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.GetOrder(id)
                .then((res) => {
                    dispatch({
                        type: 'GET_ORDER_DETAILS_SUCCESS',
                        order: res,
                        needToUpdate: needToUpdate,
                    });
                    setState && setState({ ...res, orderItems: _.cloneDeep(res.orderItems) });
                    if (setInitialState) {
                        setInitialState({ ...res, orderItems: _.cloneDeep(res.orderItems) });
                    }
                })
                .catch((e) => {
                    dispatch({
                        type: 'GET_ORDER_DETAILS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    setOrderDetails:
        (order: OrderDetails, keepPrevious?: boolean): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_ORDER_DETAILS',
                order: order,
                keepPrevious: keepPrevious,
            });
        },
    resetOrderDetails: (): AppThunkAction<OrdersAction> => (dispatch) => {
        dispatch({
            type: 'RESET_ORDER_DETAILS',
        });
    },
    getOverdudedOrders: (): AppThunkAction<OrdersAction> => (dispatch) => {
        agent.Order.GetOverdudedOrders().then((result) => {
            dispatch({
                type: 'SET_OVERDUDED_ORDERS',
                orders: result,
            });
        });
    },
    setOverdudedOrders:
        (orders: OverdudedOrder[]): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_OVERDUDED_ORDERS',
                orders: orders,
            });
        },
    setShowSearch:
        (showSearch: boolean, toggle: boolean = false): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_SHOW_SEARCH',
                showSearch: showSearch,
                toggle: toggle,
            });
        },
    prepareGettingDetailedOverdudedOrders:
        (orders: OverdudedOrder[]): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_SEARCH_STRING',
                data: orders.map((x) => x.orderNumber).join(', '),
            });
            dispatch({
                type: 'SET_SHOW_SEARCH',
                showSearch: true,
                toggle: false,
            });
        },
    getDetailedOverdudedOrders:
        (ordersNumbers: OverdudedOrder[], selectFirst: boolean): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'GET_ORDERS_LIST_START',
            });
            agent.Order.GetOverdudedOrdersDetails(ordersNumbers.map((x) => x.orderNumber))
                .then((result) => {
                    dispatch({
                        type: 'GET_ORDERS_LIST_SUCCESS',
                        list: result.list,
                        count: result.count,
                        overduded: true,
                    });
                    if (selectFirst) {
                        dispatch({
                            type: 'SET_ORDER_DETAILS_ID',
                            id: result.list[0]?.id,
                        });
                    }
                })
                .catch((e) => {
                    dispatch({
                        type: 'GET_ORDERS_LIST_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                });
        },
    updateOrdersReadyDishes:
        (orders: OrderReadyDishes): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'UPDATE_READY_ORDERS_DISHES',
                data: orders,
            });
        },
    setSelectedDish:
        (
            dishId: string,
            selectedDailyMenuId: string | undefined = undefined
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'SET_SELECTED_DISH',
                data: dishId,
                selectedDailyMenuId,
            });
        },
    addItemToOrder:
        (item: OrderItem, amount = 1, action?: () => void): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ADD_ITEM_TO_ORDER',
                item,
                amount,
                action,
            });
        },
    assignOrder:
        (orderId: string, restaurantId: string, userId: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ORDER_SET_RESPONSIBLE_USER_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.AssignOrder(orderId, restaurantId, userId)
                .then((res) => {
                    dispatch({
                        type: 'ORDER_SET_RESPONSIBLE_USER_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'ORDER_SET_RESPONSIBLE_USER_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    addItemToEditedOrder:
        (
            item: OrderItem | null,
            decrease: boolean = false,
            changeIngredients: boolean = false
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ORDER_ADD_ITEM_TO_ADD',
                item: item,
                decrease: decrease,
                changeIngredients: changeIngredients,
            });
        },
    setOrderPriceLevel:
        (orderId: string, priceLevelId: string): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ORDER_SET_PRICE_LEVEL_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.SetOrderPriceLevel(orderId, priceLevelId)
                .then((res) => {
                    dispatch({
                        type: 'ORDER_SET_PRICE_LEVEL_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'ORDER_SET_PRICE_LEVEL_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    setOrdersCount:
        (count: number): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ORDERS_COUNT_CHANGED',
                count: count,
            });
        },
    setNewOrdersCount:
        (count: number): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'NEW_ORDERS_COUNT_CHANGED',
                count: count,
            });
        },
    addNewOrder:
        (order: OrderDetails): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'ADD_NEW_ORDER',
                order,
            });
        },
    updateOneOrder:
        (order: OrderDetails, showClosed: boolean): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'UPDATE_ONE_ORDER',
                order,
                showClosed,
            });
        },
    deliverOrderItems:
        (orderId: string, ids: string[] = []): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'DELIVERY_ORDER_ITEMS_START',
            });
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.DeliverItems(orderId, ids)
                .then((res) => {
                    dispatch({
                        type: 'DELIVERY_ORDER_ITEMS_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'DELIVERY_ORDER_ITEMS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    PrintOrder:
        (
            orderId: string,
            itemsUniqueIds?: string[],
            dailyMenuId?: string,
            status?: string
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.PrintOrder(orderId, itemsUniqueIds, dailyMenuId, status)
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    PrintMultipleOrders:
        (
            orderIds?: string[],
            status?: string,
            workAreas?: string[],
            position?: string
        ): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.PrintMultipleOrders(orderIds, status, workAreas, position)
                .catch((e) => {
                    globalStore.actionCreators.showToaster(
                        'error',
                        i18n.t('common.somethingWrong')
                    )(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    UpdateOrderService:
        (id: string, service: OrderServices): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            agent.Order.UpdateOrderService(id, service.toString())
                .then((res) => {
                    dispatch({
                        type: 'UPDATE_ORDER_SERVICE_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    StornoOrderItems:
        (orderId: string, items: StornoOrderItemModel[]): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            globalStore.actionCreators.showSpiner()(dispatch);
            dispatch({
                type: 'STORNO_ORDER_ITEMS_START',
            });
            agent.Order.StornoOrderItems(orderId, items)
                .then((res) => {
                    dispatch({
                        type: 'STORNO_ORDER_ITEMS_SUCCESS',
                        order: res,
                    });
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('messages.updatedSuccesfully')
                    )(dispatch);
                })
                .catch((e) => {
                    dispatch({
                        type: 'STORNO_ORDER_ITEMS_ERROR',
                    });
                    globalStore.actionCreators.showToaster('error', e)(dispatch);
                })
                .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
        },
    SetShowStornoModal:
        (state: boolean): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({ type: 'SET_SHOW_STORNO_MODAL', payload: state });
        },
    overdudedOrderUpdated:
        (order: OverdudedOrder): AppThunkAction<OrdersAction> =>
        (dispatch) => {
            dispatch({
                type: 'OVERDUDED_ORDER_UPDATED',
                payload: order,
            });
        },
};
